import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { vortxTypography } from '@vortx/shared-ui';
import { alpha } from '@mui/material/styles';
import { COLORS, fadeInUp } from '../results/ResultsDesignTokens';
import PatternsTab from './PatternsTab';
import AnalysisTab from './AnalysisTab';
import * as learningApi from '../../../services/learningService';

const TABS = [
    { id: 'patterns', label: 'Patterns' },
    { id: 'analysis', label: 'Analysis' },
];

const TabButton = ({ label, isActive, onClick }) => (
    <Box
        onClick={onClick}
        sx={{
            px: 2,
            py: 1,
            cursor: 'pointer',
            borderBottom: '2px solid',
            borderBottomColor: isActive ? COLORS.accent : 'transparent',
            transition: 'all 0.2s ease',
            '&:hover': {
                borderBottomColor: isActive ? COLORS.accent : alpha(COLORS.accent, 0.4),
            },
        }}
    >
        <Typography sx={{
            fontFamily: vortxTypography.mono,
            fontSize: '0.75rem',
            fontWeight: isActive ? 600 : 400,
            color: isActive ? COLORS.accent : COLORS.text.secondary,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            textShadow: isActive ? `0 0 8px ${alpha(COLORS.accent, 0.6)}` : 'none',
            transition: 'all 0.2s ease',
        }}>
            {label}
        </Typography>
    </Box>
);

export default function LearningSection() {
    const [activeTab, setActiveTab] = useState('patterns');
    const [patterns, setPatterns] = useState([]);
    const [analyses, setAnalyses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchPatterns = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await learningApi.getPatterns();
            setPatterns(data.patterns || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchAnalyses = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const data = await learningApi.getAnalyses(20);
            setAnalyses(data.analyses || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchPatterns();
        fetchAnalyses();
    }, [fetchPatterns, fetchAnalyses]);

    const renderTab = () => {
        if (loading) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress size={28} sx={{ color: COLORS.accent }} />
                </Box>
            );
        }
        switch (activeTab) {
            case 'patterns':
                return <PatternsTab patterns={patterns} />;
            case 'analysis':
                return <AnalysisTab analyses={analyses} />;
            default:
                return <PatternsTab patterns={patterns} />;
        }
    };

    return (
        <Box sx={{ animation: `${fadeInUp} 0.4s ease-out` }}>
            <Typography sx={{
                fontFamily: vortxTypography.heading,
                fontSize: '1.5rem',
                fontWeight: 700,
                color: COLORS.text.primary,
                mb: 1,
            }}>
                Coordination Learning
            </Typography>
            <Typography sx={{
                fontFamily: vortxTypography.mono,
                fontSize: '0.75rem',
                color: COLORS.text.secondary,
                mb: 3,
            }}>
                Learned coordination patterns and post-run scenario analysis
            </Typography>

            {/* Tab Bar */}
            <Box sx={{
                display: 'flex',
                gap: 0,
                borderBottom: `1px solid ${alpha(COLORS.accent, 0.2)}`,
                mb: 3,
            }}>
                {TABS.map(tab => (
                    <TabButton
                        key={tab.id}
                        label={tab.label}
                        isActive={activeTab === tab.id}
                        onClick={() => setActiveTab(tab.id)}
                    />
                ))}
            </Box>

            {/* Error banner */}
            {error && (
                <Box sx={{
                    p: 2,
                    mb: 2,
                    bgcolor: alpha(COLORS.loss, 0.1),
                    border: `1px solid ${alpha(COLORS.loss, 0.3)}`,
                    borderRadius: 1,
                }}>
                    <Typography sx={{
                        fontFamily: vortxTypography.mono,
                        fontSize: '0.75rem',
                        color: COLORS.loss,
                    }}>
                        {error}
                    </Typography>
                </Box>
            )}

            {renderTab()}
        </Box>
    );
}
